import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useSelectionListKeyHandler } from "src/hooks/ticket/selection-list-key-handler/useSelectionListKeyHandler";
import { boldenMatchedSubstring } from "src/utils/Common";
import { IAccount, IAccountTag } from "src/stores/krediflow/InvoiceTypes";
import { ErpType } from "src/network/User";
import { LOADING_ACCOUNT_DATA_STATE } from "src/stores/krediflow/AccountingAccountSearchStore";
import styles from "./AccountField.module.css";
import { INVOICE_ACCOUNT_UNIT_OF_MEASURE } from "src/stores/krediflow/InvoiceEnums";

interface IAccountFieldProps {
    accountingRowIndex: number;
}

const AccountFieldBase = (props: IAccountFieldProps) => {
    const { t } = useTranslation();
    const {
        accountingRealestateSearchStore,
        accountingAccountSearchStore,
        invoiceStore,
        authStore,
        accountingCostcenterStore
    } = useContext(RootStoreContext);

    const { accountingRowIndex } = props;

    const [displayAccountList, setDisplayAccountList] = useState<boolean>(false);
    const [lastScrollPositionYAccounItems, setLastSrollYPositionAccountItems] = useState<number>(0);
    const [focusedTagIndex, setFocusedTagIndex] = useState<number>(-1);

    const accountInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (
            accountingCostcenterStore.triggerFocusPrevInput.isActive &&
            accountingCostcenterStore.triggerFocusPrevInput.index === accountingRowIndex
        ) {
            accountInputRef.current?.focus();
            accountingCostcenterStore.setTriggerFocusPrevInput(false, accountingRowIndex);
        }

        // This useEffect only listens to changes in accountingAccountSearchStore.triggerFocusNextInput
        // eslint-disable-next-line
    }, [accountingCostcenterStore.triggerFocusPrevInput]);

    useEffect(() => {
        if (
            accountingRealestateSearchStore.triggerFocusNextAccountingAccountInput.isActive &&
            accountingRealestateSearchStore.triggerFocusNextAccountingAccountInput.index === accountingRowIndex
        ) {
            accountInputRef.current?.focus();
            accountingRealestateSearchStore.setTriggerFocusNextAccountingAccountInput(false);
        }
        // This useEffect only listens to changes in accountingRealestateSearchStore.triggerFocusNextAccountingAccountInput
        // eslint-disable-next-line
    }, [accountingRealestateSearchStore.triggerFocusNextAccountingAccountInput]);

    useEffect(() => {
        if (
            accountingAccountSearchStore.triggerFocusAccountInput.isActive &&
            accountingAccountSearchStore.triggerFocusAccountInput.index === accountingRowIndex
        ) {
            accountInputRef.current?.focus();
            accountingAccountSearchStore.setTriggerFocusAccountInput(false, accountingRowIndex);
        }
        // This useEffect only listens to changes in accountingAccountSearchStore.triggerFocusAccountInput
        // eslint-disable-next-line
    }, [accountingAccountSearchStore.triggerFocusAccountInput]);

    const resetSearchQuery = useCallback(() => {
        const currentSelectedAccountQueryString =
            accountingAccountSearchStore.getSelectedAccountQueryString(accountingRowIndex);

        accountingAccountSearchStore.setCurrentAccountSearchQuery(
            currentSelectedAccountQueryString ?? "",
            accountingRowIndex
        );
    }, [accountingAccountSearchStore, accountingRowIndex]);

    useEffect(() => {
        const handleClickOutsideAccountInput = (event: any) => {
            const target = event.target as HTMLElement;

            const isClickOutsideAccountInputOrList = () => {
                if (!displayAccountList || !accountInputRef.current) {
                    return true;
                }

                const clickedInsideInput = accountInputRef.current.contains(target);

                const clickedDropdownList =
                    target.classList.contains("dropdown-list") && target.classList.contains("accounting-account");

                const clickedListItem =
                    target.classList.contains("list-item") && target.classList.contains("accounting-account");

                const clickedIcon = target.localName === "path" || target.localName === "svg";

                const clickedHighlightedText =
                    target.classList.contains("highlighted-text") && target.classList.contains("accounting-account");

                const clickedListItemChildElement =
                    target.parentElement?.classList.contains("list-item") &&
                    target.parentElement.classList.contains("accounting-account");

                return !(
                    clickedDropdownList ||
                    clickedInsideInput ||
                    clickedListItem ||
                    clickedListItemChildElement ||
                    clickedIcon ||
                    clickedHighlightedText
                );
            };

            if (isClickOutsideAccountInputOrList()) {
                setDisplayAccountList(false);

                if (
                    invoiceStore.currentInvoice?.isAccountingAccountSelected(accountingRowIndex) &&
                    accountingAccountSearchStore.getCurrentAccountSearchQuery(accountingRowIndex) === ""
                ) {
                    invoiceStore.resetAccountingAccountFormFields(accountingRowIndex);
                } else {
                    resetSearchQuery();
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutsideAccountInput);

        return () => {
            document.removeEventListener("mousedown", handleClickOutsideAccountInput);
        };
    }, [
        displayAccountList,
        resetSearchQuery,
        accountingAccountSearchStore,
        invoiceStore.currentInvoice?.isAccountingRealestateSelected,
        accountingRowIndex,
        invoiceStore
    ]);

    // Handle 'ESC' to close account list (if cursor is in account field)
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && document.activeElement === accountInputRef.current) {
                event.preventDefault();
                setDisplayAccountList(false);
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const updateAccountingWithSelectedAccount = useCallback(
        async (account: IAccount) => {
            invoiceStore.updateAccountingWithSelectedAccount(account, true, accountingRowIndex);
        },
        [invoiceStore, accountingRowIndex]
    );

    const onSelectAccountItem = useCallback(
        (activeListItemIndex: number) => {
            const selectedAccountItem =
                accountingAccountSearchStore.filteredAccountData(accountingRowIndex)[activeListItemIndex];

            const account = getAccountObject(selectedAccountItem);

            updateAccountingWithSelectedAccount(account);
        },
        [updateAccountingWithSelectedAccount, accountingAccountSearchStore, accountingRowIndex]
    );

    const onTagFocus = useCallback((index: number) => {
        setFocusedTagIndex(index);
    }, []);

    const onTagBlur = useCallback(() => {
        setFocusedTagIndex(-1);
    }, []);

    const getOnTagFocus = useCallback((index: number) => () => onTagFocus(index), [onTagFocus]);

    const onTabFromAccountField = useCallback(() => {
        // First check if account list is displayed and has exactly 1 item
        if (accountingAccountSearchStore.filteredAccountData(accountingRowIndex).length === 1) {
            const onlyAccountInList = accountingAccountSearchStore.filteredAccountData(accountingRowIndex)[0];
            updateAccountingWithSelectedAccount(onlyAccountInList);
            return;
        }

        // If account list is not displayed but account tags are visible, handle tab navigation through tags
        const accountTags = accountingAccountSearchStore.displayedAccountTagsByIndex[accountingRowIndex];
        const areTagsDisplayed =
            !invoiceStore.currentInvoice?.isAccountingAccountSelected(accountingRowIndex) &&
            accountTags &&
            accountTags.length > 0;

        if (!displayAccountList && areTagsDisplayed) {
            const nextTagIndex = focusedTagIndex + 1;
            if (nextTagIndex < accountTags.length) {
                // Move to next tag
                setFocusedTagIndex(nextTagIndex);
            } else {
                // We've reached the end of tags, move focus to next input
                setFocusedTagIndex(-1);
                accountingAccountSearchStore.setTriggerFocusNextInput(true, accountingRowIndex);
            }
            return;
        }

        // Default behavior: move to next input
        accountingAccountSearchStore.setTriggerFocusNextInput(true, accountingRowIndex);
    }, [
        accountingAccountSearchStore,
        accountingRowIndex,
        updateAccountingWithSelectedAccount,
        displayAccountList,
        focusedTagIndex,
        invoiceStore.currentInvoice,
        setFocusedTagIndex
    ]);

    const { onKeyDownFunction, setActiveListItemIndex, NO_LIST_ITEM_SELECTED } = useSelectionListKeyHandler({
        listName: "accounting-account",
        totalNumberOfDisplayedItems: invoiceStore.numberOfDisplayedItems,
        setIsListDisplayed: setDisplayAccountList,
        isListDisplayed: displayAccountList,
        onSelectItem: onSelectAccountItem,
        resetSearchQuery: resetSearchQuery,
        additionalTabActionWithNoItemSelected: onTabFromAccountField
    });

    const resetAmountOfItemsDisplayedInAccountList = useCallback(() => {
        setLastSrollYPositionAccountItems(0);
        invoiceStore.setNumberOfDisplayedItems(invoiceStore.sliceSizeOfDisplayedItems);
    }, [invoiceStore]);

    const onChangeAccountInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            resetAmountOfItemsDisplayedInAccountList();

            const inputAccount = e.target.value;

            if (inputAccount === "") {
                invoiceStore.resetAccountingAccountFormFields(accountingRowIndex);
            } else {
                accountingAccountSearchStore.setCurrentAccountSearchQuery(inputAccount, accountingRowIndex);
            }
        },
        [accountingAccountSearchStore, resetAmountOfItemsDisplayedInAccountList, accountingRowIndex, invoiceStore]
    );

    const onFocusAccountInput = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (
                invoiceStore.currentInvoice?.isAccountingAccountSelected(accountingRowIndex) &&
                accountInputRef.current
            ) {
                accountInputRef.current.select();
            }

            accountingAccountSearchStore.loadAccountData(accountingRowIndex);

            resetAmountOfItemsDisplayedInAccountList();
            setDisplayAccountList(true);
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
        },
        [
            resetAmountOfItemsDisplayedInAccountList,
            setActiveListItemIndex,
            NO_LIST_ITEM_SELECTED,
            invoiceStore.currentInvoice,
            accountingRowIndex,
            accountingAccountSearchStore
        ]
    );

    const onClickAccountItem = useCallback(
        (accountData: IAccount) => async (e: any) => {
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
            setDisplayAccountList(false);

            const account = getAccountObject(accountData);

            updateAccountingWithSelectedAccount(account);
        },
        [setActiveListItemIndex, NO_LIST_ITEM_SELECTED, updateAccountingWithSelectedAccount]
    );

    const onClickAccountTag = useCallback(
        (accountTag: IAccountTag) => async () => {
            const account = await accountingAccountSearchStore.getAccountById(accountTag.accountId, accountingRowIndex);

            accountingAccountSearchStore.setDisplayAllAccountTags(false, accountingRowIndex);

            if (account) {
                updateAccountingWithSelectedAccount(account);
            }
        },
        [updateAccountingWithSelectedAccount, accountingAccountSearchStore, accountingRowIndex]
    );

    const onTagKeyDown = useCallback(
        (index: number) => (event: React.KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                const accountTags = accountingAccountSearchStore.displayedAccountTagsByIndex[accountingRowIndex];
                if (accountTags && accountTags[index]) {
                    onClickAccountTag(accountTags[index])();
                }
            } else if (event.key === "Escape") {
                event.preventDefault();
                setFocusedTagIndex(-1);
                accountInputRef.current?.focus();
            }
        },
        [accountingAccountSearchStore, accountingRowIndex, onClickAccountTag]
    );

    const onClickDisplayAllAccountTags = useCallback(() => {
        accountingAccountSearchStore.setDisplayAllAccountTags(true, accountingRowIndex);
    }, [accountingAccountSearchStore, accountingRowIndex]);

    const onMoreTagsKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClickDisplayAllAccountTags();
            } else if (event.key === "Escape") {
                event.preventDefault();
                setFocusedTagIndex(-1);
                accountInputRef.current?.focus();
            }
        },
        [onClickDisplayAllAccountTags]
    );

    const getAccountObject = (accountData: IAccount): IAccount => {
        const account: IAccount = {
            id: accountData.id,
            number: accountData.number,
            name: accountData.name,
            requiresCostCenter: accountData.requiresCostCenter,
            requiresQuantity: accountData.requiresQuantity,
            unitOfMeasure: accountData.unitOfMeasure ?? INVOICE_ACCOUNT_UNIT_OF_MEASURE.ZERO,
            extraCostDate: accountData.extraCostDate,
            vatConfig: accountData.vatConfig,
            defaultVatCodeId: accountData.defaultVatCodeId,
            validFrom: accountData.validFrom,
            validTo: accountData.validTo,
            realestateId: accountData.realestateId
        };

        return account;
    };

    const onScrollAccountList = useCallback(
        (e: React.BaseSyntheticEvent) => {
            const windowHeight = e.target.clientHeight;
            const pixelsFromTop = e.target.scrollTop;
            const totalHeight = e.target.scrollHeight;

            const is150PixelsFromBottom = totalHeight - pixelsFromTop < windowHeight + 150;
            const isScrollingDown = pixelsFromTop > lastScrollPositionYAccounItems;

            const hasMoreResulsToDisplay =
                invoiceStore.numberOfDisplayedItems <
                accountingAccountSearchStore.filteredAccountData(accountingRowIndex).length;

            if (is150PixelsFromBottom && isScrollingDown && hasMoreResulsToDisplay) {
                invoiceStore.setNumberOfDisplayedItems(
                    invoiceStore.numberOfDisplayedItems + invoiceStore.sliceSizeOfDisplayedItems
                );
            }

            setLastSrollYPositionAccountItems(pixelsFromTop);
        },
        [lastScrollPositionYAccounItems, invoiceStore, accountingAccountSearchStore, accountingRowIndex]
    );

    const accountSelectionList = (
        <ul
            id="accounting-account-dropdown-list"
            className={`dropdown-list accounting-account krediflow ${
                authStore.user?.erpType === ErpType.IT2 ? "IT2" : "RIMO"
            }`}
            onScroll={onScrollAccountList}
        >
            {accountingAccountSearchStore.loadingAccountDataState === LOADING_ACCOUNT_DATA_STATE.LOADING ? (
                <li className="d-flex align-items-center justify-content-center list-item accounting-account loading">
                    <div>
                        <UI.RotatingSpinner noLogo size={20} />
                    </div>
                </li>
            ) : (
                accountingAccountSearchStore
                    .filteredAccountData(accountingRowIndex)
                    .slice(0, invoiceStore.numberOfDisplayedItems)
                    .map((account, index) => {
                        const accountName = `${account.number} ${account.name}`;

                        const currentAccountSearchQuery =
                            accountingAccountSearchStore.getCurrentAccountSearchQuery(accountingRowIndex);

                        const formattedAccountName = boldenMatchedSubstring(
                            currentAccountSearchQuery ?? "",
                            accountName,
                            "highlighted-text accounting-account"
                        );

                        return (
                            <li
                                key={index}
                                className="d-flex align-items-center list-item accounting-account"
                                onClick={onClickAccountItem(account)}
                                id={`accounting-account-list-item-${index}`}
                            >
                                <div
                                    className={`accounting-account-text ${styles.AccountingAccountText}`}
                                    dangerouslySetInnerHTML={{
                                        __html: formattedAccountName
                                    }}
                                />
                            </li>
                        );
                    })
            )}
        </ul>
    );

    let accountTags: IAccountTag[] = accountingAccountSearchStore.displayedAccountTagsByIndex[accountingRowIndex];

    let accountTagElements = accountTags
        ? accountTags.map((accountTag, index) => {
              return (
                  <span
                      key={index}
                      className={styles.AccountTagButton}
                      onClick={onClickAccountTag(accountTag)}
                      tabIndex={0}
                      onFocus={getOnTagFocus(index)}
                      onBlur={onTagBlur}
                      onKeyDown={onTagKeyDown(index)}
                  >
                      {`${accountTag.number} ${accountTag.name}`}
                  </span>
              );
          })
        : undefined;

    if (accountingAccountSearchStore.hasMoreAccountTagsToDisplay(accountingRowIndex) && accountTagElements) {
        accountTagElements.push(
            <span
                key={"displayMoreTags"}
                className={styles.AccountTagButton}
                onClick={onClickDisplayAllAccountTags}
                tabIndex={0}
                onKeyDown={onMoreTagsKeyDown}
            >
                ...
            </span>
        );
    }

    const accountTagsContainerElement = <div className={styles.AccountTagContainer}>{accountTagElements}</div>;

    return (
        <>
            <UI.Input
                ref={accountInputRef}
                label={t("screens.kredi_flow.accountings.account").toString()}
                autoComplete="off"
                type="text"
                value={accountingAccountSearchStore.getCurrentAccountSearchQuery(accountingRowIndex) ?? ""}
                onChange={onChangeAccountInput}
                onFocus={onFocusAccountInput}
                onKeyDown={onKeyDownFunction}
                disabled={!invoiceStore.currentInvoice?.canEditAccountingAccountField(accountingRowIndex)}
            />
            {displayAccountList ? accountSelectionList : undefined}
            {!invoiceStore.currentInvoice?.isAccountingAccountSelected(accountingRowIndex)
                ? accountTagsContainerElement
                : undefined}
        </>
    );
};

export const AccountField = Sentry.withProfiler(observer(AccountFieldBase));
